import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import {
  RuleEntity,
  RuleSetEntity,
  RULE_SEVERITIES,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  useRulesByRuleSet,
  useSelectPopover,
} from '@internal/backstage-plugin-devops-react';
import {
  affectedCount,
  maxSavingOf,
  pillClass,
  ruleTitle,
  severityOf,
  severityRank,
} from './shared';
import styles from './entity-page.module.css';

type StatusFilter = 'Failed' | 'Unknown' | 'Passed' | 'All';
const TABLE_STEP = 14;

/**
 * RuleSet page Rules tab, 1:1 with design/ruleset-page-mockup.html: the
 * compliance table scoped to one framework — Rule (name + code +
 * description), worst-of-resources severity, status, affected, saving;
 * status ToggleButtonGroup (Failed by default), search, severity
 * multi-select, "Show more" batches. Rows navigate to the rule pages.
 */
export function RuleSetRules() {
  const { entity } = useEntity();
  const ruleSet = entity as RuleSetEntity;
  const navigate = useNavigate();
  const { rules } = useRulesByRuleSet(ruleSet.metadata.name);
  const ruleEntities = useMemo(() => rules as RuleEntity[], [rules]);

  const [status, setStatus] = useState<StatusFilter>('Failed');
  const [selectedSeverities, setSelectedSeverities] = useState<Set<string>>(
    new Set(),
  );
  const severitySelect = useSelectPopover();
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(TABLE_STEP);

  const filteredRules = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ruleEntities
      .filter(rule => {
        if (status !== 'All' && (rule.spec.result ?? 'Unknown') !== status) {
          return false;
        }
        if (
          selectedSeverities.size > 0 &&
          !selectedSeverities.has(severityOf(rule))
        ) {
          return false;
        }
        if (q) {
          const haystack = [
            ruleTitle(rule),
            rule.spec.type ?? '',
            rule.metadata.description ?? '',
          ]
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(q)) {
            return false;
          }
        }
        return true;
      })
      .sort(
        (a, b) =>
          severityRank(severityOf(a)) - severityRank(severityOf(b)) ||
          affectedCount(b) - affectedCount(a),
      );
  }, [ruleEntities, status, selectedSeverities, query]);

  const toggleSeverity = (severity: string) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev);
      if (next.has(severity)) {
        next.delete(severity);
      } else {
        next.add(severity);
      }
      return next;
    });
  };

  const hiddenCount = Math.max(0, filteredRules.length - visibleCount);

  return (
    <>
      <div className={styles.toolbar} role="toolbar" aria-label="Framework rules filters">
        <ToggleButtonGroup
          aria-label="Status"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([status])}
          onSelectionChange={keys => {
            const next = [...keys][0] as StatusFilter | undefined;
            setStatus(next ?? 'Failed');
          }}
        >
          {(['Failed', 'Unknown', 'Passed', 'All'] as StatusFilter[]).map(
            value => (
              <ToggleButton key={value} id={value} size="small">
                {value}
              </ToggleButton>
            ),
          )}
        </ToggleButtonGroup>

        <label className={styles.search}>
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search rules…"
            aria-label="Search rules"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </label>

        <button
          type="button"
          ref={severitySelect.triggerRef}
          className={styles.selectBtn}
          onClick={severitySelect.toggle}
          aria-expanded={severitySelect.isOpen}
          aria-haspopup="true"
        >
          Severity
          {selectedSeverities.size > 0 && (
            <span className={styles.selectBtnCount}>
              {selectedSeverities.size}
            </span>
          )}{' '}
          <span className={styles.selectBtnArrow}>▼</span>
        </button>
        <Popover {...severitySelect.popoverProps} placement="bottom start" hideArrow>
            <div className={styles.sevPopover}>
              {(RULE_SEVERITIES as readonly string[]).map(severity => (
                <label key={severity}>
                  <input
                    type="checkbox"
                    checked={selectedSeverities.has(severity)}
                    onChange={() => toggleSeverity(severity)}
                  />
                  <span
                    className={`${styles.pill} ${pillClass(styles, severity)}`}
                  >
                    <span className={styles.pillDot} />
                    {severity}
                  </span>
                </label>
              ))}
            </div>
        </Popover>

        <span className={styles.resultNote}>
          showing {Math.min(visibleCount, filteredRules.length)} of{' '}
          {filteredRules.length}
        </span>
      </div>

      {ruleEntities.length === 0 ? (
        <div className={`${styles.card} ${styles.empty}`}>
          <div className={styles.emptyBig}>§</div>
          <b>No rules are mapped to this framework yet</b>
          Rules appear here once the report maps them to the ruleset.
        </div>
      ) : (
        <div className={styles.tableCard}>
          <div className={styles.card}>
            <table
              className={`${styles.table} ${styles.tableWide}`}
              aria-label="Rules in this framework"
            >
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Affected</th>
                  <th>Est. saving</th>
                  <th aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {filteredRules.slice(0, visibleCount).map(rule => {
                  const saving = maxSavingOf(rule);
                  return (
                    <tr
                      key={rule.metadata.name}
                      className={styles.row}
                      onClick={() =>
                        navigate(`/catalog/default/rule/${rule.metadata.name}`)
                      }
                    >
                      <td>
                        <span className={styles.ruleName}>
                          {ruleTitle(rule)}
                        </span>
                        <br />
                        <span className={styles.ruleCode}>
                          {rule.spec.type}
                        </span>{' '}
                        <span
                          className={styles.ruleDesc}
                          title={rule.metadata.description}
                        >
                          {rule.metadata.description ?? ''}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`${styles.pill} ${pillClass(styles, severityOf(rule))}`}
                        >
                          <span className={styles.pillDot} />
                          {severityOf(rule)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`${styles.pill} ${pillClass(styles, rule.spec.result ?? 'Unknown')}`}
                        >
                          <span className={styles.pillDot} />
                          {rule.spec.result}
                        </span>
                      </td>
                      <td className={styles.num}>
                        {affectedCount(rule) || '—'}
                      </td>
                      <td>
                        {saving ? (
                          <span className={styles.saving}>${saving}/mo</span>
                        ) : (
                          <span className={styles.muted}>—</span>
                        )}
                      </td>
                      <td className={styles.cellArr}>↗</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hiddenCount > 0 && (
            <button
              type="button"
              className={styles.showMoreBtn}
              onClick={() => setVisibleCount(count => count + TABLE_STEP)}
            >
              Show 14 more ({hiddenCount} hidden)
            </button>
          )}
        </div>
      )}
    </>
  );
}
